import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  Combobox,
  Option,
  Spinner,
  Button,
  Input,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Add20Regular } from '@fluentui/react-icons';
import {
  fetchCompanies,
  createCustomer,
  type Customer,
} from '../services/customerService';
import { queryKeys } from '../lib/queryClient';

interface CompanyPickerProps {
  selectedId: string | null;
  selectedName?: string;
  onChange: (companyId: string | null, companyName: string) => void;
  /** Disabled while a parent save is in flight, etc. */
  disabled?: boolean;
  /** Show a red error border when true (e.g. required-but-empty validation). */
  error?: boolean;
}

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
  },
  comboboxRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    // On narrow screens the combobox + "New" button don't fit side by side,
    // so wrap "New" onto its own line below the combobox.
    '@media (max-width: 768px)': {
      flexWrap: 'wrap',
    },
  },
  combobox: {
    flexGrow: 1,
    // Fluent's Combobox has an intrinsic ~250px min-width; without this the
    // flex row can't shrink on narrow screens.
    minWidth: 0,
    // Take the full row on mobile so the "New" button wraps beneath it.
    '@media (max-width: 768px)': {
      flexBasis: '100%',
    },
  },
  errorRing: {
    '& [role="combobox"]': {
      ...shorthands.outline('1px', 'solid', tokens.colorPaletteRedBorder1),
    },
  },
  createField: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    width: '100%',
    marginTop: '8px',
  },
  // Snug surface — the dialog only houses a single name input, so the
  // default ~600px DialogSurface width feels disproportionate.
  createDialogSurface: {
    maxWidth: '420px',
    width: '420px',
  },
  createLabel: {
    fontSize: '14px',
    fontWeight: 600,
  },
});

/**
 * Picker for the Linked Company on a Contact customer. Combobox with
 * typeahead over existing Company customers, plus an inline "Create new
 * company" flow that creates a minimal Company record and selects it.
 *
 * Both the Combobox options list and the create flow invalidate the
 * customers query cache on success so the new Company shows up everywhere.
 */
const CompanyPicker: React.FC<CompanyPickerProps> = ({
  selectedId,
  selectedName,
  onChange,
  disabled,
  error,
}) => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');

  const { data: companies = [], isLoading } = useQuery({
    queryKey: ['linked-company-options'],
    queryFn: fetchCompanies,
    staleTime: 5 * 60 * 1000,
  });

  // Filter clientside; the dataset is bounded (TWM has hundreds, not millions
  // of company customers) so typeahead doesn't need a server roundtrip.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) => c.name.toLowerCase().includes(q));
  }, [companies, search]);

  // Combobox `value` is the visible text. We keep it in sync with selection
  // OR with the user's typeahead input. Selection wins until they start
  // typing; the open-state effect resets to the selected name on close.
  const displayValue = selectedId
    ? (companies.find((c) => c.id === selectedId)?.name || selectedName || '')
    : search;

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      createCustomer({ name, customerKind: 'company', customerType: 'Company' }),
    onSuccess: (created: Customer) => {
      // Close the Dialog FIRST so Fluent's focus-trap teardown runs against
      // a quiet tree. If we kick off invalidations and the parent onChange
      // before close, the resulting re-render storm + Combobox option refetch
      // races focus restoration and ends up reclaiming focus from the next
      // field the user clicks.
      setCreateOpen(false);
      setNewCompanyName('');
      // Defer the list invalidation + parent state update to the next tick so
      // the Dialog's exit animation/focus restoration completes first.
      setTimeout(() => {
        onChange(created.id, created.name);
        queryClient.invalidateQueries({ queryKey: ['linked-company-options'] });
        queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      }, 0);
    },
  });

  return (
    <div className={styles.wrapper}>
      <div className={`${styles.comboboxRow} ${error ? styles.errorRing : ''}`}>
        <Combobox
          className={styles.combobox}
          size="large"
          freeform
          placeholder={isLoading ? 'Loading companies…' : 'Search or pick a company…'}
          value={displayValue}
          selectedOptions={selectedId ? [selectedId] : []}
          disabled={disabled || isLoading}
          onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
          onOptionSelect={(_e, data) => {
            const id = data.optionValue;
            if (!id) return;
            const company = companies.find((c) => c.id === id);
            onChange(id, company?.name || '');
            setSearch('');
          }}
        >
          {filtered.map((c) => (
            <Option key={c.id} value={c.id} text={c.name}>
              {c.name}
            </Option>
          ))}
          {filtered.length === 0 && !isLoading && (
            <Option key="__empty__" value="" disabled>
              No matching companies
            </Option>
          )}
        </Combobox>
        <Button
          appearance="secondary"
          icon={<Add20Regular />}
          onClick={() => {
            setNewCompanyName(search.trim());
            setCreateOpen(true);
          }}
          disabled={disabled}
        >
          New
        </Button>
      </div>

      <Dialog
        open={createOpen}
        onOpenChange={(_e, data) => {
          if (!data.open) {
            setCreateOpen(false);
            setNewCompanyName('');
          }
        }}
      >
        <DialogSurface className={styles.createDialogSurface}>
          <DialogBody>
            <DialogTitle>Create new company</DialogTitle>
            <DialogContent>
              <div className={styles.createField}>
                <label className={styles.createLabel}>Name</label>
                <Input
                  size="large"
                  style={{ width: '100%' }}
                  value={newCompanyName}
                  onChange={(_e, data) => setNewCompanyName(data.value)}
                  placeholder="e.g. Acme Construction"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newCompanyName.trim() && !createMutation.isPending) {
                      createMutation.mutate(newCompanyName.trim());
                    }
                  }}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={() => createMutation.mutate(newCompanyName.trim())}
                disabled={!newCompanyName.trim() || createMutation.isPending}
                icon={createMutation.isPending ? <Spinner size="tiny" /> : undefined}
              >
                {createMutation.isPending ? 'Creating…' : 'Create'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default CompanyPicker;
